/**
 * cache/layout.ts — assembly: mission → space → CacheLayoutV1.
 *
 * SEED DISCIPLINE. Every stage draws from its OWN sub-stream,
 * `rngFromString("<site>:<epoch>:<attempt>:<stage>")`, so changing one stage's rules
 * does not reshuffle the others' draws. Without this, a tweak to prop placement would
 * silently reroll every layout in the game.
 *
 * REROLL. Placement can fail (a node with no free neighbour, a corridor that would cut
 * through a third room). Failure is not repaired — the attempt counter advances and the
 * whole thing regenerates from a fresh sub-seed. Attempts are bounded; exhausting them
 * throws, because it means a cycle definition cannot be seated on its grid and no
 * amount of retrying will help.
 *
 * BIOMES. The theme is chosen EARLY, before any structure, and constrains the pools the
 * dressing layer draws from LATE. That ordering is Unexplored's, and it is why one
 * generator can produce three aesthetics without three generators.
 */
import { rngFromString, rangeInt, type Rng } from "../rng.js";
import { buildMission, chooseCycle } from "./cycles.js";
import type { MissionGraph } from "./mission.js";
import { solveMission } from "./solve.js";
import { embedMission, reachableRooms, type SpaceResult } from "./space.js";
import {
  CACHE_LAYOUT_VERSION,
  type CacheLayoutV1, type CacheProp, type CachePropKind, type CacheSpawn, type CacheTheme,
} from "./types.js";

/**
 * How many placements to try before giving up on a (site, epoch).
 *
 * Measured over 1500 tier-1 seeds: 71% seat on the first attempt, mean 1.4, and the
 * worst observed seed needed 9. Tier 0 seats first-attempt 100% of the time. 24 is
 * therefore ~2.7× the worst case seen — generation is pure integer math and costs
 * almost nothing, so the headroom is far cheaper than a hard failure in production.
 */
const MAX_ATTEMPTS = 24;

/** Encounter budget per unit of node pressure, by tier. */
const PRESSURE_BUDGET = [3, 4] as const;

export interface CacheGenInput {
  site: string;
  epoch: number;
  theme: CacheTheme;
  /** 0 = short run, 1+ = long run. Clamped to the tiers the cycle library supports. */
  tier: number;
}

export class CacheGenError extends Error {}

/**
 * Generate the layout for one cache instance. Pure and deterministic: the same input
 * always yields a deep-equal layout, on the client and on the server, which is why no
 * cache geometry is ever transmitted or persisted.
 */
export function generateCache(input: CacheGenInput): CacheLayoutV1 {
  const tier = Math.min(1, Math.max(0, Math.floor(input.tier)));
  const base = `${input.site}:${input.epoch}`;
  const failures: string[] = [];

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const seed = (stage: string): Rng => rngFromString(`${base}:${attempt}:${stage}`);

    const def = chooseCycle(seed("cycle"), tier);
    const mission = buildMission(def, seed("mission"), tier);

    // Structural assertion, not a filter — see solve.ts.
    const solution = solveMission(mission);
    if (!solution.ok) {
      throw new CacheGenError(`cycle "${def.id}" produced an unsolvable mission: ${solution.reason}`);
    }

    const diag: string[] = [];
    const space = embedMission(mission, seed("space"), diag);
    if (space === null) { failures.push(`${def.id}: ${diag[diag.length - 1] ?? "placement"}`); continue; }

    // Geometric reachability: can a body physically walk to every room?
    const walkable = reachableRooms(space, mission.entrance);
    if (walkable.size !== space.rooms.length) {
      failures.push(`${def.id}: ${space.rooms.length - walkable.size} room(s) walled off`);
      continue;
    }

    // Floor terraces. Assigned onto the space layer's rooms (it is theme-blind and
    // the ladder is not) BEFORE the layout is handed out, so `room.floorH` is never
    // observable in its placeholder state.
    const floors = roomFloorHeights(space.rooms, space.edges, mission.entrance, input.theme, base);
    for (let i = 0; i < space.rooms.length; i++) space.rooms[i].floorH = floors[i];

    return {
      v: CACHE_LAYOUT_VERSION,
      site: input.site,
      epoch: input.epoch,
      theme: input.theme,
      tier,
      cycle: def.id,
      grid: space.grid,
      cell: space.cell,
      rooms: space.rooms,
      edges: space.edges,
      spawns: buildSpawns(mission, tier),
      props: buildProps(mission, space, seed("props")),
      entrance: mission.entrance,
      boss: mission.boss,
      extent: space.extent,
    };
  }

  throw new CacheGenError(
    `cache ${base} (tier ${tier}) failed ${MAX_ATTEMPTS} placements: ${failures.join("; ")}`,
  );
}

/**
 * The terrace ladder's shape, per theme. These ARE the client dressers' own constants
 * (`ruin` 1.6/0.45, `vault` 1.4/0.4, `hollow` 1.5/0.4, all `dropMin` 1.2) moved here
 * verbatim: promoting the ladder without promoting its numbers would have re-terraced
 * every cache in the game.
 *
 * `jitter` stays under half `step` so two rooms one BFS level apart can never invert.
 */
export const FLOOR_LADDER: Record<CacheTheme, { step: number; jitter: number; dropMin: number }> = {
  ruin:   { step: 1.6, jitter: 0.45, dropMin: 1.2 },
  vault:  { step: 1.4, jitter: 0.4,  dropMin: 1.2 },
  hollow: { step: 1.5, jitter: 0.4,  dropMin: 1.2 },
};

/**
 * Room floor heights as a BFS-depth ladder from the entrance, gently jittered.
 *
 * Depth-based (rather than a random walk along the BFS tree) because BFS guarantees
 * adjacent rooms differ by at most one step — a random walk lets two rooms joined by a
 * NON-tree edge land many metres apart and puts a cliff in the corridor between them.
 * The entrance is lowest, so `shortcut-loop`'s one-way `drop` back to it descends by
 * construction. Result is normalised so the lowest floor sits at 0.
 *
 * ⚠ THE SEED IS THE DRESSER'S OWN STREAM, `<site>:<epoch>:<theme>`, and the jitter is
 * its FIRST n−1 draws. That is not decoration: this ladder was the client dressers'
 * opening move (P4–P7), and reproducing their stream position for position is what
 * makes the promotion byte-equal instead of a silent re-roll of every dungeon. The
 * dressers still SPEND those draws (see `roomFloorHeights` in theme/common.ts) so
 * every later draw from the stream — noise tables, headroom, undulation — is unmoved.
 */
export function roomFloorHeights(
  rooms: readonly { readonly i: number }[],
  edges: readonly { readonly a: number; readonly b: number; readonly kind: string }[],
  entrance: number,
  theme: CacheTheme,
  base: string,
): number[] {
  const o = FLOOR_LADDER[theme];
  const rng = rngFromString(`${base}:${theme}`);
  const n = rooms.length;

  const adj: number[][] = rooms.map(() => []);
  edges.forEach((e, ei) => { adj[e.a].push(ei); adj[e.b].push(ei); });
  const depth = new Int32Array(n).fill(-1);
  depth[entrance] = 0;
  const queue = [entrance];
  for (let qi = 0; qi < queue.length; qi++) {
    const cur = queue[qi];
    for (const ei of adj[cur]) {
      const nx = edges[ei].a === cur ? edges[ei].b : edges[ei].a;
      if (depth[nx] === -1) { depth[nx] = depth[cur] + 1; queue.push(nx); }
    }
  }

  const floorH = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const jitter = i === entrance ? 0 : (rng() * 2 - 1) * o.jitter;
    floorH[i] = Math.max(0, depth[i]) * o.step + jitter;
  }
  // One-way `drop` edges must genuinely descend a→b. Bounded relaxation (a drop cycle
  // would be a cycle-library bug, and the bound keeps this terminating either way).
  for (let pass = 0; pass < n + 1; pass++) {
    let moved = false;
    for (const e of edges) {
      if (e.kind !== "drop") continue;
      if (floorH[e.b] > floorH[e.a] - o.dropMin) { floorH[e.b] = floorH[e.a] - o.dropMin; moved = true; }
    }
    if (!moved) break;
  }
  let lowest = Infinity;
  for (let i = 0; i < n; i++) lowest = Math.min(lowest, floorH[i]);
  for (let i = 0; i < n; i++) floorH[i] -= lowest;
  return floorH;
}

/**
 * Encounter SLOTS, not encounters. Phase 5's encounters.ts turns a budget into concrete
 * drone kinds; keeping the split means the expressive-range harness can score leniency
 * before a single enemy exists.
 */
function buildSpawns(m: MissionGraph, tier: number): CacheSpawn[] {
  const per = PRESSURE_BUDGET[tier];
  const out: CacheSpawn[] = [];
  for (const n of m.nodes) {
    if (n.role === "entrance" || n.pressure <= 0) continue;
    out.push({ room: n.i, budget: n.pressure * per });
  }
  return out;
}

/** Prop slots by role. Abstract kinds only — the theme picks the mesh. */
function buildProps(m: MissionGraph, space: SpaceResult, rng: Rng): CacheProp[] {
  const out: CacheProp[] = [];
  for (const n of m.nodes) {
    const room = space.rooms[n.i];
    const kinds: CachePropKind[] = [];
    if (n.role === "treasure" || n.role === "key") kinds.push("loot", "light");
    if (n.role === "arena" || n.role === "boss") kinds.push("cover", "cover", "light");
    if (n.role === "gate") kinds.push("marker", "light");
    if (n.role === "vista") kinds.push("marker");
    if (n.role === "transit" && n.pressure >= 2) kinds.push("hazard");
    if (n.role === "entrance") kinds.push("marker", "light");
    for (const kind of kinds) {
      // Offsets stay inside the footprint; yaw is DATA (no trig is taken in shared).
      const span = room.w * 0.35;
      out.push({
        room: n.i,
        kind,
        u: (rng() * 2 - 1) * span,
        v: (rng() * 2 - 1) * span,
        yaw: rangeInt(rng, 0, 7) * 0.7853981633974483, // 45° steps
      });
    }
  }
  return out;
}
